import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ENVIRONMENT_TYPES, type EnvironmentType } from '@system-atlas/shared';
import { ApiError, api, type ImageCategory, type ImageMeta } from '../lib/api';
import { Alert, Button, Modal, Select, Spinner, TextInput } from '../components/ui';
import { cn } from '../lib/utils';

interface Section {
  id: string;
  title: string;
  images: ImageMeta[];
}

/**
 * Links angedockte Bild-Bibliothek (ersetzt das frühere Modal):
 * Suchfeld, Akkordeon je Kategorie + „Ohne Kategorie" + „Eigene",
 * Klick fügt ein (Panel bleibt offen), Upload als kleines Modal.
 */
export function ImageLibraryPanel({
  onClose,
  onInsert,
}: {
  onClose: () => void;
  onInsert: (image: ImageMeta) => void;
}) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ImageCategory[]>([]);
  const [images, setImages] = useState<ImageMeta[] | null>(null);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const [cats, imgs] = await Promise.all([api.listImageCategories(), api.listImages({ scope: 'all' })]);
      setCategories(cats.categories);
      setImages(imgs.images);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const searching = search.trim().length > 0;

  const sections = useMemo<Section[]>(() => {
    const all = images ?? [];
    const term = search.trim().toLowerCase();
    const matches = (img: ImageMeta) => !term || img.name.toLowerCase().includes(term);
    const result: Section[] = [];
    const sortedCats = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const cat of sortedCats) {
      const imgs = all.filter((i) => i.isLibrary && i.categoryId === cat.id && matches(i));
      if (imgs.length > 0 || !term) result.push({ id: cat.id, title: cat.name, images: imgs });
    }
    const uncategorized = all.filter((i) => i.isLibrary && !i.categoryId && matches(i));
    if (uncategorized.length > 0 || !term) {
      result.push({ id: '__none__', title: t('images.noCategory'), images: uncategorized });
    }
    const mine = all.filter((i) => !i.isLibrary && matches(i));
    if (mine.length > 0 || !term) result.push({ id: '__mine__', title: t('images.scopeMine'), images: mine });
    return result;
  }, [images, categories, search, t]);

  const toggleSection = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <aside className="flex h-full w-full flex-col border-r border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center gap-2 border-b border-slate-200 p-2 dark:border-slate-700">
        <span className="flex-1 text-sm font-semibold">{t('images.libraryTitle')}</span>
        <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => setUploadOpen(true)}>
          {t('images.upload')}
        </Button>
        <button
          type="button"
          aria-label={t('common.close')}
          title={t('common.close')}
          onClick={onClose}
          className="rounded px-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          ×
        </button>
      </div>
      <div className="p-2">
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('images.searchPlaceholder')}
          className="text-sm"
        />
      </div>
      {error && (
        <div className="px-2 pb-2">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {!images ? (
          <Spinner />
        ) : (
          sections.map((section) => {
            const isOpen = searching || !collapsed.has(section.id);
            return (
              <div key={section.id}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleSection(section.id)}
                  className="flex w-full items-center gap-1 px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  <span className="w-4 text-slate-400">{isOpen ? '▾' : '▸'}</span>
                  <span className="flex-1 truncate normal-case">{section.title}</span>
                  <span className="font-normal">{section.images.length}</span>
                </button>
                {isOpen && (
                  <div className={cn('grid grid-cols-3 gap-1 px-2 pb-2', section.images.length === 0 && 'hidden')}>
                    {section.images.map((img) => (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => onInsert(img)}
                        title={img.name}
                        className="rounded-md border border-slate-200 p-1 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-400"
                      >
                        <img src={api.imageRawUrl(img.id)} alt={img.name} className="h-12 w-full object-contain" />
                        <span className="block truncate text-center text-[10px] text-slate-600 dark:text-slate-300">
                          {img.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {isOpen && section.images.length === 0 && (
                  <p className="px-3 pb-2 text-xs text-slate-400">{t('images.empty')}</p>
                )}
              </div>
            );
          })
        )}
      </div>

      <UploadModal
        open={uploadOpen}
        categories={categories}
        onClose={() => setUploadOpen(false)}
        onUploaded={async () => {
          setUploadOpen(false);
          await load();
        }}
      />
    </aside>
  );
}

function UploadModal({
  open,
  categories,
  onClose,
  onUploaded,
}: {
  open: boolean;
  categories: ImageCategory[];
  onClose: () => void;
  onUploaded: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [uploadName, setUploadName] = useState('');
  const [uploadEnv, setUploadEnv] = useState<EnvironmentType>('neutral');
  const [uploadCategory, setUploadCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async (e: FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.uploadImage(file, {
        name: uploadName || file.name,
        environmentType: uploadEnv,
        categoryId: uploadCategory || undefined,
      });
      setUploadName('');
      if (fileRef.current) fileRef.current.value = '';
      await onUploaded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title={t('images.uploadTitle')} onClose={onClose}>
      <form onSubmit={upload} className="space-y-3">
        {error && <Alert kind="error">{error}</Alert>}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="block w-full text-sm"
        />
        <div className="grid grid-cols-2 gap-2">
          <TextInput placeholder={t('images.name')} value={uploadName} onChange={(e) => setUploadName(e.target.value)} />
          <Select value={uploadEnv} onChange={(e) => setUploadEnv(e.target.value as EnvironmentType)}>
            {ENVIRONMENT_TYPES.map((env) => (
              <option key={env} value={env}>
                {t(`environment.${env}`)}
              </option>
            ))}
          </Select>
        </div>
        {categories.length > 0 && (
          <Select className="w-full" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
            <option value="">{t('images.noCategory')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('images.upload')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
